// Source-aware classifier for workout_sessions rows.
//
// Two shapes coexist on the same table:
//   - gym       — manual VYV workouts. exercises = [{ name, sets:[{weight,reps,done}…] }]
//                 source defaults to 'manual'.
//   - activity  — Strava (and future Garmin/Whoop) imports. exercises =
//                 [{ type, distance_meters, average_speed_mps }]. source='strava'.
//
// The shape mismatch breaks any code that assumes `.sets` (counts a Strava
// run as zero-volume gym workout). Use these helpers anywhere that reads
// `workout_sessions.exercises`.

export type SessionKind = "gym" | "activity";

export interface WorkoutSessionRow {
  id: string;
  user_id?: string;
  plan_id?: string | null;
  name: string | null;
  source: string | null;
  exercises: unknown;
  started_at?: string | null;
  completed_at?: string | null;
  duration_minutes?: number | null;
  calories_burned?: number | null;
  notes?: string | null;
  external_id?: string | null;
  exercises_completed?: number | null;
  sets_completed?: number | null;
  total_sets?: number | null;
}

// Fontes cujo `exercises` vem na forma ACTIVITY (distância/velocidade), não
// na forma gym (séries/repetições).
//
// apple_health / health_connect / google_health entraram em 29/07 junto com o
// sync nativo. Sem eles aqui o treino importado do relógio cairia no ramo
// "gym": a tela contaria `sets` que não existem (→ 0), mostraria a pílula
// azul de academia num treino de corrida, e o volume em FitnessProgress
// somaria zero. Um lugar só conserta classificação, gate e cálculo.
const ACTIVITY_SOURCES = new Set([
  "strava", "garmin", "whoop", "fitbit", "oura",
  "apple_health", "health_connect", "google_health",
]);

interface ActivityExercise {
  type?: string;
  distance_meters?: number;
  average_speed_mps?: number;
}

interface GymSet {
  weight?: number | string;
  reps?: number | string;
  done?: boolean;
}

interface GymExercise {
  timed?: boolean;
  name?: string;
  muscle?: string;
  muscle_group?: string;
  sets?: GymSet[];
}

export function getSessionKind(row: { source?: string | null; exercises?: unknown }): SessionKind {
  if (row.source && ACTIVITY_SOURCES.has(row.source)) return "activity";

  // Defensive shape sniff for legacy rows with NULL source — if the first
  // exercise carries distance/speed fields, treat it as an activity even
  // when source isn't set.
  if (Array.isArray(row.exercises) && row.exercises.length > 0) {
    const first = row.exercises[0] as ActivityExercise | null;
    if (first && (
      typeof first.distance_meters === "number" ||
      typeof first.average_speed_mps === "number"
    )) {
      return "activity";
    }
  }
  return "gym";
}

export function isGymSession(row: { source?: string | null; exercises?: unknown }): boolean {
  return getSessionKind(row) === "gym";
}

export function isActivitySession(row: { source?: string | null; exercises?: unknown }): boolean {
  return getSessionKind(row) === "activity";
}

// ── Activity helpers (Strava et al) ────────────────────────────────────────

function firstActivity(row: WorkoutSessionRow): ActivityExercise | null {
  if (!Array.isArray(row.exercises) || row.exercises.length === 0) return null;
  return (row.exercises[0] ?? null) as ActivityExercise | null;
}

export function getActivityDistanceKm(row: WorkoutSessionRow): number | null {
  const ex = firstActivity(row);
  if (!ex || typeof ex.distance_meters !== "number" || !Number.isFinite(ex.distance_meters)) return null;
  return ex.distance_meters / 1000;
}

export function getActivityType(row: WorkoutSessionRow): string | null {
  const ex = firstActivity(row);
  return ex && typeof ex.type === "string" && ex.type.length > 0 ? ex.type : null;
}

export function getActivityAverageSpeedKph(row: WorkoutSessionRow): number | null {
  const ex = firstActivity(row);
  if (!ex || typeof ex.average_speed_mps !== "number" || !Number.isFinite(ex.average_speed_mps)) return null;
  return ex.average_speed_mps * 3.6;
}

// Pace as min/km — useful for runs. Returns "MM:SS" or null when distance/
// duration are missing or zero.
export function getActivityPaceMinPerKm(row: WorkoutSessionRow): string | null {
  const km = getActivityDistanceKm(row);
  const mins = row.duration_minutes;
  if (!km || km <= 0 || !mins || mins <= 0) return null;
  const totalSec = (mins * 60) / km;
  const m = Math.floor(totalSec / 60);
  const s = Math.round(totalSec - m * 60);
  return m.toString().padStart(2, "0") + ":" + s.toString().padStart(2, "0");
}

// ── Gym helpers (manual VYV workouts) ──────────────────────────────────────

// Sum of weight × reps for every DONE set in the session. Returns 0 for
// activity-kind rows by construction (their exercises have no `sets`).
export function getGymVolumeKg(row: WorkoutSessionRow): number {
  if (getSessionKind(row) !== "gym") return 0;
  if (!Array.isArray(row.exercises)) return 0;
  let total = 0;
  for (const exRaw of row.exercises) {
    const ex = exRaw as GymExercise;
    if (!ex || ex.timed || !Array.isArray(ex.sets)) continue;
    for (const s of ex.sets) {
      if (!s || s.done !== true) continue;
      const w = typeof s.weight === "number" ? s.weight : Number(s.weight);
      const r = typeof s.reps === "number" ? s.reps : Number(s.reps);
      if (!Number.isFinite(w) || !Number.isFinite(r)) continue;
      total += w * r;
    }
  }
  return total;
}

export function getGymExerciseCount(row: WorkoutSessionRow): number {
  if (getSessionKind(row) !== "gym" || !Array.isArray(row.exercises)) return 0;
  return row.exercises.length;
}

export function getGymSetCount(row: WorkoutSessionRow): { done: number; total: number } {
  if (getSessionKind(row) !== "gym" || !Array.isArray(row.exercises)) return { done: 0, total: 0 };
  let done = 0;
  let total = 0;
  for (const exRaw of row.exercises) {
    const ex = exRaw as GymExercise;
    if (!ex || !Array.isArray(ex.sets)) continue;
    total += ex.sets.length;
    for (const s of ex.sets) {
      if (s && s.done === true) done++;
    }
  }
  return { done, total };
}
